import { call, all, put, takeLatest, take, select } from 'redux-saga/effects';
import AsyncStorage from '@react-native-community/async-storage';

import history from '../../../services/history';
import notification from '../../../services/notification';
import authService from '../../../services/modules/auth/auth-service';
import teamService from '~/services/modules/team/team-service';
import { formatMessage } from '../../../i18n/translate';
import {
  ISA_TOKEN,
  ISA_USER,
  ISA_TENANT_ID,
  ISA_CONFIGURATION,
  ISA_ERROR,
  ORDER_RECOVERY_KEY,
  ISA_USER_SELECTED_FOR_CONTEXT,
  USER_ROLE,
  ISA_ORIGINAL_USER_TOKEN
} from '../../../constants';
import {
  LOGIN_REQUEST,
  LOGOUT_REQUEST,
  RESET_PASSWORD_REQUEST,
  REFRESH_REQUEST,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_CANCEL,
  CHANGE_PASSWORD_REQUEST
} from './auth-constants';
import * as authActions from './auth-actions';
import * as configurationActions from '../configuration/configuration-actions';
import * as tenantActions from '../tenant/tenant-actions';
import * as notificationActions from '../notifications/notifications-actions';
import userService from '~/services/modules/user/user-service';
import { AppStateType } from '../root-reducer';

export function* login({ payload }: any) {
  try {
    if (payload.email === '' || payload.password === '') {
      yield call(notification.warning, formatMessage('loginMessageError'));
      return;
    }

    yield put(authActions.setLoading(true));

    const toast: any = notification.loading(formatMessage('loginMesageLoading'));

    const { data } = yield call(authService.login, payload);
    if (data) {
      if (data?.payload?.passwordResetNextLogin) {
        toast.close();
        yield put(authActions.openChangePasswordModal(data?.token, payload.password));
        const action = yield take([CHANGE_PASSWORD_SUCCESS, CHANGE_PASSWORD_CANCEL]);
        if (action.type === CHANGE_PASSWORD_CANCEL) {
          yield put(authActions.setLoading(false));
          return;
        }
      }
      yield call(AsyncStorage.setItem, ISA_TOKEN, data?.token);
      yield call(AsyncStorage.setItem, ISA_TENANT_ID, data?.payload?.tenantID);
      yield call(AsyncStorage.setItem, ISA_ORIGINAL_USER_TOKEN, data?.token);

      let hierarchyTree = null;

      const { data: team } = yield call(teamService.getUserTeam);

      if (data?.payload?.userRole !== USER_ROLE.VENDOR) {
        const { data: hierarchyTreeData } = yield call(userService.getHierarchyTree);
        hierarchyTree = hierarchyTreeData?.hierarchyTree;
      }

      const user = { ...data.payload, team, hierarchyTree };

      yield call(AsyncStorage.setItem, ISA_USER, JSON.stringify(user));

      yield put(
        authActions.loginSucess({
          token: data?.token,
          tenantID: data?.payload?.tenantID,
          user
        })
      );
      toast.close();

      yield put(configurationActions.getRequest());
      yield put(tenantActions.getTenantRequest());
      yield put(notificationActions.getAllRequest());

      yield call(history.push, '/');
      yield call(notification.success, formatMessage('loginMessageSuccess'));
    }
    yield put(authActions.setLoading(false));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(authActions.setLoading(false));
  }
}

export function* refreshToken() {
  try {
    const { data } = yield call(authService.refresh);

    if (data) {
      yield call(AsyncStorage.setItem, ISA_TOKEN, data?.token);
    }

    yield put(authActions.setLoading(false));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(authActions.setLoading(false));
  }
}

export function* resetPassword({ email }: any) {
  try {
    if (email === '') {
      notification.warning(formatMessage('resetPasswordMessageError'));
      return;
    }

    yield put(authActions.setLoading(true));

    yield call(authService.resetPassword, email);
    yield put(authActions.setLoading(false));
    yield put(authActions.resetPasswordSuccess());
  } catch (error) {
    yield put(authActions.setLoading(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* changePassword({ newPassword }: any) {
  try {
    const { token, oldPassword } = yield select((state: AppStateType) => state?.auth);
    yield put(authActions.changePasswordLoading(true));

    yield call(authService.changePassword, { oldPassword, newPassword, token });
    yield put(authActions.changePasswordLoading(false));
    yield put(authActions.changePasswordSuccess());
  } catch (error) {
    yield put(authActions.changePasswordLoading(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* logout() {
  yield call(AsyncStorage.removeItem, ISA_TOKEN);
  yield call(AsyncStorage.removeItem, ISA_TENANT_ID);
  yield call(AsyncStorage.removeItem, ISA_USER);
  yield call(AsyncStorage.removeItem, ISA_CONFIGURATION);
  yield call(AsyncStorage.removeItem, ORDER_RECOVERY_KEY);
  yield call(AsyncStorage.removeItem, ISA_USER_SELECTED_FOR_CONTEXT);
  yield call(AsyncStorage.removeItem, ISA_ORIGINAL_USER_TOKEN);

  yield put(authActions.logoutSuccess());
  yield call(history.push, '/auth/login');
}

export default all([
  takeLatest(LOGIN_REQUEST, login),
  takeLatest(RESET_PASSWORD_REQUEST, resetPassword),
  takeLatest(CHANGE_PASSWORD_REQUEST, changePassword),
  takeLatest(LOGOUT_REQUEST, logout),
  takeLatest(REFRESH_REQUEST, refreshToken)
]);
